import { throwError } from "./error-handling";
import { DIAGRAM_EVENTS } from "../config/events.const";
import { setCanvasSize } from "./diagram-positioning-state";
import { DiagramUtils } from "./DiagramUtils";
import { getMousePositionRelativeToCanvasContext, updateScaleFactor } from "../../utils/utils";

/**
 * @description Diagram event listeners class
 */
export class DiagramEventListeners {
    activeKeys = new Map();

    fabricDiagramCtx;

    container;

    cursorIsOutsideCanvas = false;

    /**
     * @constructor
     * @param {FabricDiagram} fabricDiagramCtx - fabric diagram instance
     */
    constructor(fabricDiagramCtx) {
        this.fabricDiagramCtx = fabricDiagramCtx;
        this.addListeners(fabricDiagramCtx.canvas);
        // call the function manually at initialization to set the initial width and height
        this.setContainer();
        this.onWindowResize();
    }

    /**
     * @description Sets the diagram container element ref
     */
    setContainer() {
        if (!this.container && this.fabricDiagramCtx.config.containerID) {
            this.container = document.querySelector(`#${this.fabricDiagramCtx.config.containerID}`);
        }
    }

    /**
     * @description Window resize event handler
     */
    onWindowResize() {
        const diagram = this.fabricDiagramCtx;
        if (!this.container && diagram.config.containerID) this.container = document.querySelector(`#${diagram.config.containerID}`);
        if (!this.container) {
            throwError("Canvas container not found! Make sure you are sending the correct ID!");
        }
        setCanvasSize(diagram.canvas, this.container.clientWidth, window.innerHeight ? window.innerHeight - 88 : this.container.clientHeight);
        updateScaleFactor(this.fabricDiagramCtx, window.devicePixelRatio);
        this.fabricDiagramCtx.requestViewRedraw(null, true);
    }

    /**
     * @description Mouse wheel event handler
     */
    onMouseWheelEvent($event) {
        const payload = $event;
        const event = DIAGRAM_EVENTS.MOUSE_WHEEL;
        const coords = this.getMousePositionRelativeToCanvasContext($event);

        this.fabricDiagramCtx.sendPluginRelatedEvent(event, { payload, coords });
    }

    /**
     * @description Click event handler
     */
    onMouseClickEvent($event) {
        $event.preventDefault();
        $event.stopPropagation();
        const payload = this.getMousePositionRelativeToCanvasContext($event);
        const event = DIAGRAM_EVENTS.CLICK;

        this.fabricDiagramCtx.sendPluginRelatedEvent(event, payload);
    }

    /**
     * @description Drag leave handler
     */
    onDragleaveEvent($event) {
        $event.preventDefault();
        $event.stopPropagation();
        const payload = this.getMousePositionRelativeToCanvasContext($event);
        const event = DIAGRAM_EVENTS.DRAG_LEAVE;

        this.fabricDiagramCtx.sendPluginRelatedEvent(event, payload);
    }

    /**
     * @description Drop event handler
     */
    onDropEvent(ev) {
        ev.preventDefault();
        ev.stopPropagation();
        const event = DIAGRAM_EVENTS.DROP;
        const isValidDrag = this.isValidDrag(ev);
        if (!isValidDrag) {
            return;
        }
        let data = ev.dataTransfer.getData("data");
        try {
            data = JSON.parse(data);
        } catch (e) {
            console.error("Invalid drop event payload");
        }
        const hasOwnState = this.getHasOwnStateFromEvent(ev);
        const isContainer = this.getDragContainerStatus(ev);
        let container = {};
        if (hasOwnState && isContainer) {
            container = this.getContainerSize(isContainer);
        }

        this.fabricDiagramCtx.sendPluginRelatedEvent(event, { ...data, hasOwnState, ...container });
    }

    /**
     * @description Dragover datatransfer types. Returns hasownstate property value
     */
    getHasOwnStateFromEvent($event) {
        return $event.dataTransfer.types.indexOf("hasownstate") !== -1;
    }

    /**
     * @description Dragover datatransfer types. Returns validplugin property value
     */
    isValidDrag($event) {
        return $event.dataTransfer.types.indexOf("validplugin") !== -1;
    }

    /**
     * @description Dragover datatransfer types. Returns their parsed values
     */
    getDragContainerStatus($event) {
        return $event.dataTransfer.types.find(type => type.indexOf("width") !== -1 && type.indexOf("height") !== -1);
    }

    /**
     * @description Dragover Datatransfer types. Returns data from type string.
     */
    getContainerSize(containerVal) {
        let splitValues = containerVal.split(";");
        splitValues = splitValues.map(val => {
            const splitProp = val.split(":");
            const returnVal = {};
            returnVal[splitProp[0]] = parseInt(splitProp[1]);
            return returnVal;
        });
        return { ...splitValues[0], ...splitValues[1] };
    }

    /**
     * @description Drag over event handler.
     */
    onDragoverEvent($event) {
        $event.preventDefault();
        const event = DIAGRAM_EVENTS.DRAG_OVER;
        // parses event types on datatransfer. Its a workaround to transfer data as object properties through the dragover event
        const hasOwnState = this.getHasOwnStateFromEvent($event);
        const isContainer = this.getDragContainerStatus($event);
        let container = {};
        const isValidDrag = this.isValidDrag($event);
        if (!isValidDrag) {
            this.onMouseMoveEvent($event);
            return;
        }
        if (hasOwnState && isContainer) {
            container = this.getContainerSize(isContainer);
        }

        const payload = {
            ...this.getMousePositionRelativeToCanvasContext($event), ...{ event: $event }, hasOwnState, ...container
        };
        this.fabricDiagramCtx.sendPluginRelatedEvent(event, payload);
    }

    /**
     * @description Key down event handler
     */
    onKeydownEvent($event) {
        this.activeKeys.set($event.key, true);
        const event = DIAGRAM_EVENTS.KEYDOWN;
        const payload = $event;
        this.fabricDiagramCtx.sendPluginRelatedEvent(event, payload);
    }

    /**
     * @description Key up event handler
     */
    onKeyupEvent($event) {
        this.activeKeys.set($event.key, false);
    }

    /**
     * @description Mouse move event handler
     */
    onMouseMoveEvent($event) {
        $event.preventDefault();
        const event = DIAGRAM_EVENTS.MOUSE_MOVE;
        const payload = { ...this.getMousePositionRelativeToCanvasContext($event), ...{ event: $event } };
        this.fabricDiagramCtx.sendPluginRelatedEvent(event, payload);
        return false;
    }

    /**
     * @description Mouse down event handler
     */
    onMouseDownEvent($event) {
        $event.preventDefault();
        this.fabricDiagramCtx.canvas.focus();
        const event = DIAGRAM_EVENTS.MOUSE_DOWN;
        const payload = { ...this.getMousePositionRelativeToCanvasContext($event), ...{ event: $event }, ...{ fabricCtx: this.fabricDiagramCtx } };

        this.fabricDiagramCtx.sendPluginRelatedEvent(event, payload);
        return false;
    }

    /**
     * @description Double click handler
     */
    onMouseDblClickEvent($event) {
        $event.preventDefault();
        $event.stopPropagation();
        const event = DIAGRAM_EVENTS.MOUSE_DBL_CLICK;
        const payload = { ...this.getMousePositionRelativeToCanvasContext($event), ...{ event: $event }, ...{ fabricDiagramCtx: this.fabricDiagramCtx } };

        this.fabricDiagramCtx.sendPluginRelatedEvent(event, payload);
    }

    /**
     * @description Mouse up handler
     */
    onMouseUpEvent($event) {
        $event.preventDefault();
        $event.stopPropagation();
        const event = DIAGRAM_EVENTS.MOUSE_UP;
        const payload = { ...this.getMousePositionRelativeToCanvasContext($event), ...{ event: $event } };

        this.fabricDiagramCtx.sendPluginRelatedEvent(event, payload);
        return false;
    }

    /**
     * @description Calculates the mouse position relative to the diagram context taking into consideration the scrollbars offsets, pixel density, and container size
     */
    getMousePositionRelativeToCanvasContext($event) {
        return getMousePositionRelativeToCanvasContext(this.fabricDiagramCtx, {
            x: $event.layerX,
            y: $event.layerY
        });
    }

    /**
     * @description Mouse leave handler
     */
    onMouseLeaveEvent($event) {
        $event.preventDefault();
        $event.stopPropagation();
        const event = DIAGRAM_EVENTS.MOUSE_LEAVE;
        this.cursorIsOutsideCanvas = true;
        this.fabricDiagramCtx.sendPluginRelatedEvent(event, {});
        return false;
    }

    /**
     * @description Mouse enter handler
     */
    onMouseEnterEvent($event) {
        $event.preventDefault();
        $event.stopPropagation();
        const event = DIAGRAM_EVENTS.MOUSE_ENTER;
        this.cursorIsOutsideCanvas = false;
        this.fabricDiagramCtx.sendPluginRelatedEvent(event, {});
        return false;
    }

    /**
     * @description Mouse up handler - window - cursor is outside canvas, dragging function active
     */
    onWindowMouseUp($event) {
        $event.preventDefault();
        $event.stopPropagation();
        if (this.cursorIsOutsideCanvas) {
            const event = DIAGRAM_EVENTS.MOUSE_UP_WINDOW;
            this.cursorIsOutsideCanvas = false;
            this.fabricDiagramCtx.sendPluginRelatedEvent(event, {});
        }
        return false;
    }

    /**
     * @description Adds event listeners to the diagram
     */
    addListeners(canvas) {
        const sixtyFPS = (1000 / 60);
        const utils = new DiagramUtils();
        canvas.addEventListener("mousemove", utils.throttle(this.onMouseMoveEvent.bind(this), sixtyFPS));
        canvas.addEventListener("mousedown", this.onMouseDownEvent.bind(this));
        canvas.addEventListener("mouseup", this.onMouseUpEvent.bind(this));
        canvas.addEventListener("mouseleave", this.onMouseLeaveEvent.bind(this));
        canvas.addEventListener("mouseenter", this.onMouseEnterEvent.bind(this));
        canvas.addEventListener("click", this.onMouseClickEvent.bind(this));
        canvas.addEventListener("keydown", this.onKeydownEvent.bind(this));
        canvas.addEventListener("keyup", this.onKeyupEvent.bind(this));
        canvas.addEventListener("dblclick", this.onMouseDblClickEvent.bind(this));
        canvas.addEventListener("wheel", this.onMouseWheelEvent.bind(this), { passive: false });

        // Plugin Drag Events
        canvas.addEventListener("drop", this.onDropEvent.bind(this));
        canvas.addEventListener("dragover", this.onDragoverEvent.bind(this));
        canvas.addEventListener("dragleave", this.onDragleaveEvent.bind(this));
        // Canvas resize on window resize event
        window.addEventListener("resize", this.onWindowResize.bind(this));
        window.addEventListener("mouseup", this.onWindowMouseUp.bind(this));
    }

    /**
     * @description Removes event listeners. Used when the diagram is closed.
     */
    removeListeners(canvas) {
        if (!canvas) return;
        canvas.removeEventListener("mousemove", this.onMouseMoveEvent);
        canvas.removeEventListener("mouseleave", this.onMouseLeaveEvent);
        canvas.removeEventListener("mouseenter", this.onMouseEnterEvent);
        canvas.removeEventListener("mousedown", this.onMouseDownEvent);
        canvas.removeEventListener("mouseup", this.onMouseUpEvent);
        canvas.removeEventListener("wheel", this.onMouseWheelEvent);
        canvas.removeEventListener("dblclick", this.onMouseDblClickEvent);

        canvas.removeEventListener("click", this.onMouseClickEvent);
        canvas.removeEventListener("keydown", this.onKeydownEvent);
        canvas.removeEventListener("keyup", this.onKeyupEvent);
        window.removeEventListener("resize", this.onWindowResize);
        window.removeEventListener("mouseup", this.onWindowMouseUp);

        // Plugin Drag Events
        canvas.removeEventListener("drop", this.onDropEvent);
        canvas.removeEventListener("dragover", this.onDragoverEvent);
        canvas.removeEventListener("dragleave", this.onDragleaveEvent);
    }
}
